import {useDispatch, useSelector} from "react-redux";
import {IGlobalProduct, IState} from "../../../types/state";
import React, {FC, Fragment, MouseEvent as ReactMouseEvent, ReactNode, useEffect, useState} from "react";
import {useToast} from "../../../atoms/Toast/useToast";
import {useRouter} from "next/router";
import cls from "./customInnerPage.module.scss";
import {CheckToastSvg, FailSvg, HeartActive, HeartSvg} from "../../../svg";
import {wishlistAddItem, wishlistRemoveItem} from "../../../store/wishlist";
import Script from "next/script";
import {IProduct, ISizeChart, optionItemType, TranslationsType} from "../../../types/homePageTypes";
import CustomDetails from "./CustomDetails";
import {ICurrencies} from "../Wrappers/MainLayout";
import {ICustomerGroupPrices, IRequestFormDetails} from "../../../pages/[productSlug]";
import CustomSideBar from "./CustomSideBar";
import CustomSocialLinks from "./CustomSocialLinks";
import CustomDetailTabButtons from "./CustomDetailTabButtons";
import {
    addCartDivStyleToggleSimple,
    dangerOrSuccessStyleSimple,
    isSelectedDetailSectionClass,
    schemaProduct,
    showBoxAboutDetails,
    useFetchCartToken
} from "./helpers";
import CustomProductGallery from "./CustomProductGallery";
import InputNumber from "../../shared/InputNumber";
import CustomSelect from "../../../atoms/forProduct/CustomSelect";
import CustomMultiSelect from "../../../atoms/forProduct/CustomMultiSelect";
import CustomRadio from "../../../atoms/forProduct/CustomRadio";
import CustomCheckbox from "../../../atoms/forProduct/CustomCheckbox";
import {handleAddCard} from "../../shared/ProductCard/helpers";
import {IFormComponentType} from "../../builder/types";
import {openModalLayoutAction} from "../../../store/general";
import ModalLayout from "../../shared/ModalLayout";
import SizeChartContent from "./SizeChartContent";

interface ICustomProduct {
    dbName: string;
    domain: string;
    productSlug: string;
    selectedRate: ICurrencies;
    product: IProduct;
    customerGroupPrices: Array<ICustomerGroupPrices>;
    translationsData: TranslationsType;
    selectedLocale: string;
    schemaBrandName: string;
    showDetailsInFront: boolean;
    showBuilderComponents: (id: number) => Record<"true" | "false", ReactNode>;
    showBuilderData: boolean;
    requestFormInfo: IFormComponentType | null;
    requestFormDetails: IRequestFormDetails;
    sizeChart: ISizeChart | null
}

const CustomBundle: FC<ICustomProduct> = (
    {
        dbName,
        productSlug,
        domain,
        selectedRate,
        product,
        translationsData,
        selectedLocale,
        schemaBrandName,
        showDetailsInFront,
        showBuilderComponents,
        showBuilderData,
        requestFormInfo,
        requestFormDetails,
        sizeChart
    }
) => {
    const [clientSideProduct, setClientSideProduct] = useState<IProduct>(product);
    const {
        flats: [{
            name: productName = "",
            description: productDesc = "",
            short_description: productShortDesc = "",
            price_html: productPriceHtml = null,
        } = {}] = [],
        images: [productImgPath = ""] = [],
        id: productId,
        sku: productSku = "",
        details: productDetails = [],
        sku_option: {name: productSkuOptionName = "SKU"} = {},
        short_description_option: {name: productShortDescOptionName = ""} = {},
        description_option: {name: productDescOptionName = ""} = {},
        bundle_options
    } = product;
    const stateDefaultValue = bundle_options.map(({option_product_info}) => {
        return option_product_info?.find(findItem => !!findItem.is_default) || {} as optionItemType;
    });
    const [productQuantity, setProductQuantity] = useState<number>(1);
    const [optionQuantity, setOptionQuantity] = useState<optionItemType[]>(stateDefaultValue);
    const [errorHandleIds, setErrorHandleIds] = useState<Array<number>>([]);
    const [isSelectDetail, setIsSelectDetail] = useState<boolean>(!productDesc);
    const cartToken = useSelector((state: IState) => state.cartToken);
    const {token: customerToken} = useSelector((state: IState) => state.customer);
    const signed = useSelector((state: IState) => state.customer.authenticated);
    const wishlist = useSelector((state: IState) => state.wishlist);
    const openModalLayout = useSelector((state: IState) => state.general.openModalLayout);
    const isProductInWishList = wishlist?.find((item: IGlobalProduct) => item.id === productId)
    const [wishIcon, setWishIcon] = useState<React.JSX.Element>(<HeartSvg/>)
    const maxQty: number = Math.min(...optionQuantity.map(qtyConcat => Math.floor(qtyConcat.productQty / qtyConcat.optionDefaultQty)));
    useEffect(() => {
        setWishIcon(!!isProductInWishList ? <HeartActive className={cls["inner-wishlist"]}/> : <HeartSvg/>)
    }, [isProductInWishList]);
    const dispatch = useDispatch();
    const toast = useToast();
    const {asPath, push: routerPush} = useRouter();
    const bundleOptionIds = bundle_options.map(opt => opt.id);
    const isAnyElemNotSelected = bundleOptionIds.filter(elem => {
        return !Boolean(optionQuantity.find(({bundleOptionId}) => bundleOptionId === elem))
    })
    useEffect(() => {
        setOptionQuantity(stateDefaultValue)
    }, [asPath]);
    const addToast = (error?: boolean) => {
        if (error) {
            toast.open(
                <span className="d-flex chek-fms">
                    <FailSvg/>
                    {translationsData.ProductNotAvailable}
                </span>
            );
        } else if (!isAnyElemNotSelected.length) {
            toast.open(
                <span className="d-flex chek-fms">
                    <CheckToastSvg/>
                    {translationsData.ToastAddToCart}
               </span>
            )
        }
    }
    const schema = schemaProduct(asPath, productName, productDesc, productImgPath, productSku, productId, dbName, domain, schemaBrandName)
    const handleAmdPrice = ({code: currCode}: ICurrencies, price: number | null) => {
        if (price) {
            const currentCurrCodeIsAmdOrRub = currCode.toLowerCase() === "amd" || currCode.toLowerCase() === "rub";
            if (!currentCurrCodeIsAmdOrRub) {
                return Number(price).toFixed(2)
            }
            return +price
        }
        return null
    }
    const handlePriceFormat = (price: number | string | null) => {
        if (price) {
            return String(price).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        }
        return null
    }

    useEffect(() => {
        const totalPrice = optionQuantity.reduce((total, {optionDefaultQty, price}) => {
            return total + optionDefaultQty * price;
        }, 0);
        setClientSideProduct((prevState) => {
            return {
                ...prevState,
                flats: [{
                    min_price: totalPrice,
                    price: totalPrice
                }]
            }
        })
    }, [optionQuantity]);

    useFetchCartToken(cartToken)
    useEffect(() => {
        setProductQuantity(prevState => {
            if (maxQty < prevState) return maxQty
            return prevState
        })
    }, [maxQty]);

    const selectTab = (e: ReactMouseEvent<HTMLDivElement>) => {
        setIsSelectDetail((e.target as HTMLDivElement).id === "detail")
    }
    const handleWishIconClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        if (!signed) {
            toast.open(
                <span className="d-flex faild-toast-fms">
                    <FailSvg/>
                    {translationsData.ToastSignIn}
                </span>
            )
        } else {
            !!isProductInWishList ?
                dispatch(wishlistRemoveItem(product.id))
                : dispatch(wishlistAddItem(product, selectedLocale))
            const toastChildren = {
                "true": {svg: <FailSvg/>, title: translationsData?.ToastWishListRemove},
                "false": {svg: <CheckToastSvg/>, title: translationsData?.ToastWishList}
            }
            toast.open(
                <span className="d-flex faild-toast-fms">
                    {toastChildren[`${!!isProductInWishList}`].svg}
                    {toastChildren[`${!!isProductInWishList}`].title}
                </span>
            )
        }
    }
    const descriptions = (
        <div className="product-inner-long-description">
            <div style={{paddingLeft: "30px"}}>
                <div dangerouslySetInnerHTML={{__html: productDesc}}/>
            </div>
        </div>
    )

    const details = <CustomDetails
        details={productDetails}
        isSelectDetail={isSelectDetail}
        showDetailsInFront={showDetailsInFront}
        translationsData={translationsData}
        dbName={dbName}
        domain={domain}
    />
    const handleAddCartClickLocal = async (quantity: number = 1) => {
        setErrorHandleIds(() => isAnyElemNotSelected);
        const qtyOptionSend = (forQty: boolean) => {
            return optionQuantity.reduce((prevValue, {id, bundleOptionId, optionDefaultQty}) => {
                return {
                    ...prevValue,
                    [bundleOptionId]: forQty ? optionDefaultQty : (id as number[]).length > 0 ? id : [id]
                }
            }, {})
        }
        if (!isAnyElemNotSelected.length) {
            return await handleAddCard({
                openUpCrossProd: async () => false,
                dispatch,
                data: {
                    product,
                    quantity,
                    cartToken,
                    customer: {token: customerToken},
                    selectedLocale,
                    selectedCurrCode: selectedRate.code,
                    optionsObject: null,
                    addToast,
                    links: [],
                    bundle_options: qtyOptionSend(false),
                    bundle_option_qty: qtyOptionSend(true)
                },
                routerPush,
                isProductInStock: true,
            })
        }
    }
    const ProductPriceToggle = () => {
        const specialPrice = handlePriceFormat(handleAmdPrice(selectedRate, clientSideProduct.flats[0].special_price))
        const price = handlePriceFormat(handleAmdPrice(selectedRate, clientSideProduct.flats[0].price))
        const pricePositionHandle = (whichPrice: string | null) => {
            if (selectedRate?.code === "AMD") {
                return whichPrice + " " + selectedRate?.symbol
            }
            return selectedRate?.symbol + " " + whichPrice
        }
        const standardPriceJSX = (whichPrice: string | null) => (
            <div className={cls.price_value}>
                {pricePositionHandle(whichPrice)}
            </div>
        )
        const PriceLayout: FC = () => {
            if (specialPrice) {
                return <Fragment>
                    {[{clsKey: "new", price: specialPrice}, {clsKey: "old", price: price}]
                        .map((elem) => (
                        <div key={elem.clsKey} className={cls[`product-card__${elem.clsKey}-price`]}>
                            {standardPriceJSX(elem.price)}
                        </div>
                    ))}
                </Fragment>
            }
            return standardPriceJSX(price)
        }
        return <div className={cls.product__price}><PriceLayout/></div>
    };

    const ShowSizeChart = () => {
        if (sizeChart) {
            return <Fragment>
                <p
                    className={cls["product-inner-chart-title"]}
                    style={{ margin: "12px 0", cursor: "pointer" }}
                    onClick={() => dispatch(openModalLayoutAction(true))}
                >
                    {translationsData?.ViewSizeChart}
                </p>
                {openModalLayout
                    ? <ModalLayout close={() => dispatch(openModalLayoutAction(false))} title={sizeChart?.template_name}>
                        <SizeChartContent sizeChart={sizeChart} dbName={dbName} domain={domain}/>
                    </ModalLayout>
                    : null
                }
            </Fragment>
        }
        return null
    }
    return (
        <Fragment>
            <Script
                id={schema.name}
                type="application/ld+json"
                dangerouslySetInnerHTML={{__html: JSON.stringify(schema)}}
                defer
            />
            <div>
                <div className={cls["product__content"]}>
                    <CustomProductGallery images={product.images} dbName={dbName}/>
                    <div className={cls.product__info}>
                        <h1 style={{textAlign: "left"}} className={cls.product__name}>{productName}</h1>
                        <p className={cls.product__sku}>{productSkuOptionName}: {productSku}</p>
                        <ProductPriceToggle />
                        <ShowSizeChart />
                        <div>{productPriceHtml ? product?.flats[0]?.price_html?.map((e: string, i: number) => (
                            <span key={i} className={cls["product_price_html"]}>{e}</span>
                        )) : <Fragment/>}</div>
                        {productShortDesc?.length > 0 ? (
                            <div className={cls["product-inner-description-title"]}>
                                {productShortDescOptionName}
                            </div>
                        ) : <Fragment />}
                        <div dangerouslySetInnerHTML={{__html: productShortDesc}}/>
                        {bundle_options.map(({option_product_info, type, label, id, is_required}) => {
                            const bundleElemProps = {
                                selectArray: option_product_info,
                                setOptionQuantity: setOptionQuantity,
                                optionQuantity: optionQuantity,
                                bundleId: id,
                            }
                            const bundleComponents: Record<string, ReactNode> = {
                                select: <CustomSelect {...bundleElemProps} />,
                                radio: <CustomRadio {...bundleElemProps} />,
                                checkbox: <CustomCheckbox {...bundleElemProps} />,
                                multiselect: <CustomMultiSelect {...bundleElemProps} />
                            }
                            const filterOptionArr = optionQuantity.find(findItem => findItem.bundleOptionId === id)
                            const {productQty = 1, optionDefaultQty = 1, id: findId = null}
                                = filterOptionArr || {productQty: 1, optionDefaultQty: 1, id: null};
                            return <div className={cls.options_main} key={id}>
                                <h4>{label}<span>{is_required ? "*" : ""}</span></h4>
                                {is_required && errorHandleIds.some(elemId => elemId === id)
                                    ? <span className={cls["err-block"]}>
                                        {translationsData.RequiredField}
                                    </span>
                                    : <Fragment/>}
                                {bundleComponents[type]}
                                <div className={cls["product__sidebar"]}>
                                    <form className={cls["product__options"]}>
                                        <div className={`form-group ${cls["product__option"]}`}>
                                            <div className={cls["product__actions"]}>
                                                <div
                                                    className={`${cls["product__actions-item"]} ${cls["product-inner-quantity"]}`}>
                                                    <InputNumber
                                                        id={`${label}${id}product-quantity`}
                                                        aria-label="Quantity"
                                                        className={cls["product__quantity"]}
                                                        size="lg"
                                                        min={1}
                                                        max={productQty}
                                                        value={optionDefaultQty || 1}
                                                        onChange={(quantity: number) => {
                                                            setOptionQuantity((prevState) => {
                                                                return prevState.map(elem => {
                                                                    if (String(elem.id) === String(findId)) {
                                                                        return {
                                                                            ...elem,
                                                                            optionDefaultQty: quantity
                                                                        }
                                                                    }
                                                                    return elem
                                                                })
                                                            })
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        })}
                        <ul className={cls.product__meta}>
                            <li className={cls["product__meta-availability"]}>
                                <span
                                    className={dangerOrSuccessStyleSimple[`${!isAnyElemNotSelected.length}`].className}
                                    style={{fontSize: "18px"}}>
                                    {translationsData?.[dangerOrSuccessStyleSimple[`${!isAnyElemNotSelected.length}`].ItlKey]}
                                </span>
                            </li>
                            <div className={`${cls["product__actions-item"]} ${cls["product-inner-quantity"]}`}>
                                <InputNumber
                                    id="product-quantity"
                                    aria-label="Quantity"
                                    className={cls["product__quantity"]}
                                    size="lg"
                                    min={1}
                                    max={maxQty}
                                    value={productQuantity}
                                    onChange={(quantity: number) => {
                                        if (quantity > maxQty) {
                                            setProductQuantity(maxQty)
                                        } else {
                                            setProductQuantity(quantity)
                                        }
                                    }}
                                />
                            </div>
                        </ul>
                        <CustomSideBar
                            ProductAddToCart_INTL={translationsData?.AddToCartButtonTitle}
                            actionClassName={addCartDivStyleToggleSimple[`${!isAnyElemNotSelected.length}`]}
                            handleCardClick={handleAddCartClickLocal}
                            isButtonDisabled={false}
                            wishListButtonClick={handleWishIconClick}
                            wishIconClicked={wishIcon}
                            inpValQuantity={productQuantity}
                            requestFormInfo={requestFormInfo}
                            requestFormDetails={requestFormDetails}
                        />
                        <CustomSocialLinks dbName={dbName} domain={domain} productSlug={productSlug} translationsData={translationsData}/>
                    </div>
                </div>
                {showBuilderComponents(2)[`${showBuilderData}`]}
                <CustomDetailTabButtons
                    boxAboutDetailsClass={showBoxAboutDetails[`${showDetailsInFront}`]}
                    isSelectedDetailSectionClass={isSelectedDetailSectionClass(descriptions, details)[`${isSelectDetail}`]}
                    productDescOptionName={productDescOptionName}
                    Details_INTL={translationsData?.Details}
                    selectTab={selectTab}
                    showDescription={!!productDesc}
                    showDetailsInFront={showDetailsInFront}
                />
                {isSelectedDetailSectionClass(descriptions, details)[`${isSelectDetail}`].content}
            </div>
        </Fragment>
    );
};

export default CustomBundle;
